import type { LocalizableResource } from '~/composables/types/localizable'
import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'
import type { StrapiEventListPage } from '~/composables/types/strapiEventListPage'
import { EventListPageType } from '~/composables/types/strapiEventListPage'
import { useStrapiConfig, buildStrapiUrl } from '~/composables/useStrapi'
import { getStrapiKitsListPageUrl } from '~/composables/useStrapiKits'

const FETCH_TIMEOUT_MS = 10_000

export const useResolvedKitsListPage = (slug: string) => {
  const { locale } = useI18n()
  const strapiConfig = useStrapiConfig()

  const cacheKey = `resolved-kits-page-${slug}-${locale.value}`

  return useAsyncData<LocalizableResource | null, Error>(
    cacheKey,
    async (): Promise<LocalizableResource | null> => {
      const url = buildStrapiUrl(
        strapiConfig,
        getStrapiKitsListPageUrl({
          slug: `${slug}_${locale.value}`,
          locale: locale.value,
        })
      )

      try {
        const response = await $fetch<
          StrapiApiDefaultResponse<StrapiEventListPage[]>
        >(url, { headers: strapiConfig.headers, timeout: FETCH_TIMEOUT_MS })
        const page = response.data?.[0]
        if (!page) return null

        page.type = EventListPageType.KITS
        strapiEventListPageGetters
          .getLocalizations(page)
          ?.forEach((localization) => {
            localization.type = EventListPageType.KITS
          })

        return page as LocalizableResource
      } catch {
        return null
      }
    },
    {
      server: true,
      lazy: false,
      dedupe: 'defer',
      getCachedData: (key) => useNuxtData(key).data.value,
    }
  )
}
